import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Users, UserCircle, TruckIcon, Target, AlertTriangle, Clock, AlertCircle, PackageX } from 'lucide-react';
import BatchReceiveDialog from '@/components/BatchReceiveDialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [salesTarget, setSalesTarget] = useState(null);
  const [negativeStock, setNegativeStock] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prodSummary, setProdSummary] = useState(null);
  const [rmRequired, setRmRequired] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState({});
  const [batchReceive, setBatchReceive] = useState({ open: false, vendor: '', entries: [] });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const h = {};
      // Everyone can see basic stats, sales target, production summary and the
      // pending-vendor-receipts alert (so they can Receive Batch). Admins get
      // the additional operational widgets (negative stock, full alerts, RM req).
      const promises = [
        axios.get(`${API}/dashboard/stats`, { headers: h }),
        axios.get(`${API}/dashboard/sales-target`, { headers: h }),
        axios.get(`${API}/dashboard/production-summary`, { headers: h }),
        axios.get(`${API}/dashboard/alerts`, { headers: h }),
      ];
      if (isAdmin) {
        promises.push(axios.get(`${API}/dashboard/negative-stock`, { headers: h }));
        promises.push(axios.get(`${API}/dashboard/rm-required`, { headers: h }));
      }
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setSalesTarget(results[1].data);
      setProdSummary(results[2].data);
      setAlerts(results[3].data);
      if (isAdmin) {
        if (results[4]) setNegativeStock(results[4].data);
        if (results[5]) setRmRequired(results[5].data);
      }
    } catch (err) { console.error(err); toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="p-12 text-center text-text-muted">Loading dashboard...</div>;

  const grandScheduled = salesTarget?.customers?.reduce((s, c) => s + c.scheduled_qty, 0) || 0;
  const grandDispatched = salesTarget?.customers?.reduce((s, c) => s + c.dispatched_qty, 0) || 0;
  const grandPct = grandScheduled > 0 ? Math.round((grandDispatched / grandScheduled) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="dashboard-title">DASHBOARD</h1>
        <p className="text-sm text-text-muted mt-2">Production overview at a glance</p>
      </div>

      {/* Summary cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white border border-border rounded-sm p-4" data-testid="stat-items">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded flex items-center justify-center"><Package className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Items</p>
                <p className="text-xl font-bold font-mono">{stats.items_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="stat-vendors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded flex items-center justify-center"><Users className="w-5 h-5 text-purple-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Vendors</p>
                <p className="text-xl font-bold font-mono">{stats.vendors_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="stat-customers">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded flex items-center justify-center"><UserCircle className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Customers</p>
                <p className="text-xl font-bold font-mono">{stats.customers_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="stat-stage-stock">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded flex items-center justify-center"><Package className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Stage Stock</p>
                <p className="text-xl font-bold font-mono">{stats.stage_stock_qty?.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="stat-vendor-wip">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded flex items-center justify-center"><TruckIcon className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Vendor WIP</p>
                <p className="text-xl font-bold font-mono">{stats.vendor_wip_qty?.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Target Achievement */}

      {/* Negative Stock Alert - Admin Only */}
      {isAdmin && negativeStock && (negativeStock.rm.length > 0 || negativeStock.stage.length > 0) && (
        <div className="bg-red-50 border-2 border-red-300 rounded-sm" data-testid="negative-stock-alert">
          <div className="p-4 border-b border-red-200 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-red-800">Stock Error Report — Negative Stock Detected</h2>
          </div>
          <div className="p-4 space-y-4">
            {negativeStock.rm.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-2">Negative RM Stock</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-red-200">
                      <th className="h-8 px-3 text-left font-medium text-red-700 uppercase text-xs tracking-wider">Steel Grade</th>
                      <th className="h-8 px-3 text-left font-medium text-red-700 uppercase text-xs tracking-wider">Section</th>
                      <th className="h-8 px-3 text-right font-medium text-red-700 uppercase text-xs tracking-wider">Qty (Weight)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {negativeStock.rm.map((r, idx) => (
                      <tr key={idx} className="border-b border-red-100" data-testid={`neg-rm-row-${idx}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.steel_grade}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.section}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-bold text-red-700">{r.qty_weight?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {negativeStock.stage.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-2">Negative Stage Stock</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-red-200">
                      <th className="h-8 px-3 text-left font-medium text-red-700 uppercase text-xs tracking-wider">Part ID</th>
                      <th className="h-8 px-3 text-left font-medium text-red-700 uppercase text-xs tracking-wider">Part Name</th>
                      <th className="h-8 px-3 text-left font-medium text-red-700 uppercase text-xs tracking-wider">Stage</th>
                      <th className="h-8 px-3 text-right font-medium text-red-700 uppercase text-xs tracking-wider">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {negativeStock.stage.map((s, idx) => (
                      <tr key={idx} className="border-b border-red-100" data-testid={`neg-stage-row-${idx}`}>
                        <td className="px-3 py-2 font-mono text-xs">{s.part_id}</td>
                        <td className="px-3 py-2 text-xs">{s.part_name || '-'}</td>
                        <td className="px-3 py-2 text-xs">{s.stage}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-bold text-red-700">{s.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-red-600 italic">Negative stock indicates missing opening stock entries or data entry errors. Please review transactions.</p>
          </div>
        </div>
      )}

      {/* Production Summary - Admin Only */}
      {prodSummary && (
        <div className="bg-white border border-border rounded-sm" data-testid="production-summary">
          <div className="p-4 border-b border-border bg-gray-50/50">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Production Summary — {prodSummary.month}</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm text-center">
                <div className="text-xs text-blue-600 uppercase font-medium">Yesterday's Qty</div>
                <div className="text-2xl font-bold text-blue-700 font-mono">{prodSummary.yesterday.qty.toLocaleString()}</div>
                <div className="text-xs text-blue-500">{prodSummary.yesterday.date}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm text-center">
                <div className="text-xs text-blue-600 uppercase font-medium">Yesterday's Weight</div>
                <div className="text-2xl font-bold text-blue-700 font-mono">{prodSummary.yesterday.weight.toLocaleString()} kg</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm text-center">
                <div className="text-xs text-emerald-600 uppercase font-medium">MTD Qty</div>
                <div className="text-2xl font-bold text-emerald-700 font-mono">{prodSummary.mtd.qty.toLocaleString()}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm text-center">
                <div className="text-xs text-emerald-600 uppercase font-medium">MTD Weight</div>
                <div className="text-2xl font-bold text-emerald-700 font-mono">{prodSummary.mtd.weight.toLocaleString()} kg</div>
              </div>
            </div>
            {prodSummary.part_breakdown?.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">Part-wise Breakdown</h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                  {prodSummary.part_breakdown.map((p, i) => (
                    <div key={i} className="flex justify-between items-center text-xs border border-border p-2 rounded-sm">
                      <span className="font-mono font-semibold">{p.part_id}</span>
                      <span className="text-gray-600">{p.qty} pcs / {p.weight} kg</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* RM Required Widget (this week + next week) - Admin Only */}
      {isAdmin && rmRequired && rmRequired.groups_count > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-sm" data-testid="rm-required-widget">
          <div className="p-4 border-b border-red-300 bg-red-100/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PackageX className="w-5 h-5 text-red-700" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-red-900">
                RM Required — This Week + Next Week
              </h2>
              <span className="text-xs bg-red-200 text-red-900 px-2 py-0.5 rounded-full font-bold" data-testid="rm-required-count">
                {rmRequired.groups_count} material{rmRequired.groups_count === 1 ? '' : 's'} · {rmRequired.parts_count} part{rmRequired.parts_count === 1 ? '' : 's'}
              </span>
            </div>
            <span className="text-[11px] text-red-700">
              {rmRequired.this_week_start} → +6d · {rmRequired.next_week_start} → +6d
            </span>
          </div>
          <div className="divide-y divide-red-200">
            {rmRequired.groups.map((g, gi) => {
              const key = `${g.steel_grade}|${g.section}`;
              const expanded = !!expandedGroup[key];
              return (
                <div key={key} data-testid={`rm-group-${gi}`}>
                  <button
                    onClick={() => setExpandedGroup({ ...expandedGroup, [key]: !expanded })}
                    className="w-full p-3 flex items-center justify-between hover:bg-red-100/40 transition-colors text-left"
                    data-testid={`rm-group-toggle-${gi}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-sm text-red-900">{g.steel_grade}</span>
                      <span className="text-xs text-red-700">·</span>
                      <span className="font-mono text-xs text-red-800">{g.section}</span>
                      <span className="text-[11px] text-red-600 ml-2">{g.parts.length} part{g.parts.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div>
                        <span className="text-red-600">Pending: </span>
                        <span className="font-mono font-semibold text-red-900">{g.total_pending_pcs} pcs</span>
                      </div>
                      <div>
                        <span className="text-red-600">Required: </span>
                        <span className="font-mono font-semibold text-red-900">{g.total_kg_required.toLocaleString()} kg</span>
                      </div>
                      <div>
                        <span className="text-red-600">Short: </span>
                        <span className="font-mono font-bold text-red-900">{g.shortfall_kg.toLocaleString()} kg</span>
                      </div>
                      <span className="text-red-500">{expanded ? '▾' : '▸'}</span>
                    </div>
                  </button>
                  {expanded && (
                    <div className="bg-white border-t border-red-200 px-4 py-2" data-testid={`rm-group-details-${gi}`}>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-red-700 uppercase tracking-wider text-[10px]">
                            <th className="py-1 pr-2">Part</th>
                            <th className="py-1 pr-2">Week</th>
                            <th className="py-1 pr-2 text-right">Pending Pcs</th>
                            <th className="py-1 pr-2 text-right">Kg Need</th>
                            <th className="py-1 pr-2 text-right">Kg Have</th>
                            <th className="py-1 pr-2">Alt Sizes In Stock</th>
                            <th className="py-1 pr-2">Customers</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.parts.map((p, pi) => (
                            <tr key={`${p.part_id}-${p.week}-${pi}`} className="border-t border-red-100" data-testid={`rm-part-row-${gi}-${pi}`}>
                              <td className="py-1 pr-2 font-mono font-semibold">
                                {p.part_id}
                                {p.part_name && <span className="text-text-muted ml-2">{p.part_name}</span>}
                              </td>
                              <td className="py-1 pr-2">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.week === 'this_week' ? 'bg-red-200 text-red-900' : 'bg-orange-100 text-orange-800'}`}>
                                  {p.week === 'this_week' ? 'THIS WK' : 'NEXT WK'}
                                </span>
                              </td>
                              <td className="py-1 pr-2 text-right font-mono">{p.pending_pcs}</td>
                              <td className="py-1 pr-2 text-right font-mono">{(p.rm_required_kg || 0).toLocaleString()}</td>
                              <td className="py-1 pr-2 text-right font-mono">{(p.rm_available_kg || 0).toLocaleString()}</td>
                              <td className="py-1 pr-2 text-[11px]">
                                {p.alternates_in_stock && p.alternates_in_stock.length > 0
                                  ? p.alternates_in_stock.map(a => `${a.section} (${a.qty_weight} kg)`).join(', ')
                                  : <span className="text-red-400">none</span>}
                              </td>
                              <td className="py-1 pr-2 text-[11px]">
                                {(p.customers || []).map(c => c.name).join(', ') || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Operational Alerts - Admin gets everything; users only get Pending Vendor Receipts */}
      {alerts && (alerts.dispatch_backlogs.length > 0 || (alerts.pending_vendor_receipts || []).length > 0) && (
        <div className="space-y-4" data-testid="operational-alerts">
          <h2 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />Operational Alerts
          </h2>
          <div className="space-y-4">
            {alerts.dispatch_backlogs.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm" data-testid="alert-dispatch-backlogs">
                <div className="p-3 border-b border-red-200 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-700" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-red-800">Dispatch Plan Backlogs ({alerts.dispatch_backlogs.length})</span>
                </div>
                <div className="p-3 max-h-48 overflow-auto">
                  {alerts.dispatch_backlogs.map((a, i) => (
                    <div key={i} className="flex justify-between py-1 text-xs border-b border-red-100 last:border-0">
                      <span><span className="font-mono font-semibold text-red-900">{a.part_id}</span> <span className="text-red-600">— {a.customer_name}</span></span>
                      <span className="text-red-800 font-mono">Backlog: {a.backlog_qty} pcs</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(alerts.pending_vendor_receipts || []).length > 0 && (() => {
              // Group by vendor_name (already sorted by vendor then by days_pending desc)
              const groups = {};
              (alerts.pending_vendor_receipts || []).forEach(a => {
                const key = a.vendor_name || a.vendor_id || 'Unknown';
                if (!groups[key]) groups[key] = [];
                groups[key].push(a);
              });
              const vendorKeys = Object.keys(groups);
              return (
                <div className="bg-indigo-50 border border-indigo-200 rounded-sm w-full" data-testid="alert-pending-vendor-receipts">
                  <div className="p-4 border-b border-indigo-200 flex items-center gap-2">
                    <TruckIcon className="w-5 h-5 text-indigo-700" />
                    <span className="text-base font-semibold uppercase tracking-wider text-indigo-900">
                      Pending Vendor Receipts ({alerts.pending_vendor_receipts.length})
                    </span>
                    <span className="text-xs text-indigo-700 ml-auto">Sorted by vendor · days pending · red &gt; 5 days</span>
                  </div>
                  <div className="p-4 space-y-4">
                    {vendorKeys.map(vendorName => (
                      <div key={vendorName} className="bg-white border border-indigo-200 rounded-sm">
                        <div className="px-4 py-2 bg-indigo-100/60 border-b border-indigo-200 flex items-center justify-between">
                          <span className="text-sm font-semibold text-indigo-900" data-testid={`pending-vendor-group-${vendorName}`}>{vendorName}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-indigo-700">
                              {groups[vendorName].length} entr{groups[vendorName].length === 1 ? 'y' : 'ies'} · {groups[vendorName].reduce((s, x) => s + (x.pending_qty || 0), 0)} pcs pending
                            </span>
                            <button
                              onClick={() => setBatchReceive({ open: true, vendor: vendorName, entries: groups[vendorName] })}
                              className="px-3 py-1 bg-indigo-700 text-white text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-indigo-800"
                              data-testid={`batch-receive-btn-${vendorName}`}
                            >
                              Receive Batch
                            </button>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-indigo-50/50 text-[11px] uppercase tracking-wider text-indigo-700">
                              <th className="text-left px-4 py-2">Part ID</th>
                              <th className="text-left px-4 py-2">Part Name</th>
                              <th className="text-left px-4 py-2">Stage</th>
                              <th className="text-right px-4 py-2">Pending Qty</th>
                              <th className="text-right px-4 py-2">Days Pending</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groups[vendorName].map((a, i) => {
                              const overdue = (a.days_pending || 0) > 5;
                              return (
                                <tr key={i} className="border-t border-indigo-100" data-testid={`pending-vendor-row-${vendorName}-${i}`}>
                                  <td className="px-4 py-2 font-mono font-semibold text-indigo-900">{a.part_id}</td>
                                  <td className="px-4 py-2 text-indigo-700">{a.part_name || '—'}</td>
                                  <td className="px-4 py-2 text-indigo-700">{a.stage}</td>
                                  <td className="px-4 py-2 text-right font-mono font-semibold text-indigo-900">{a.pending_qty}</td>
                                  <td className={`px-4 py-2 text-right font-mono font-bold ${overdue ? 'text-red-700 bg-red-50' : 'text-indigo-800'}`}>{a.days_pending || 0}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Sales Target Achievement */}
      {salesTarget && salesTarget.customers && salesTarget.customers.length > 0 && (
        <div className="bg-white border border-border rounded-sm" data-testid="sales-target-section">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-accent" />
              <h2 className="text-sm font-semibold uppercase tracking-wider">Sales Target — {new Date(salesTarget.month + '-01').toLocaleDateString('en-IN', {month: 'long', year: 'numeric'})}</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-muted">Overall:</span>
              <span className={`font-mono text-sm font-bold ${grandPct >= 100 ? 'text-green-600' : grandPct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{grandPct}%</span>
              <span className="text-xs text-text-muted font-mono">{grandDispatched.toLocaleString()} / {grandScheduled.toLocaleString()}</span>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {salesTarget.customers.map((c, idx) => (
              <div key={idx} className="space-y-1.5" data-testid={`sales-target-row-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{c.customer_name}</span>
                    <span className="text-xs text-text-muted font-mono">({c.parts_count} parts)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-text-muted">{c.dispatched_qty.toLocaleString()} / {c.scheduled_qty.toLocaleString()}</span>
                    <span className={`font-mono text-xs font-bold min-w-[48px] text-right ${c.achievement_pct >= 100 ? 'text-green-600' : c.achievement_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {c.achievement_pct}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-2.5 rounded-full transition-all ${c.achievement_pct >= 100 ? 'bg-green-500' : c.achievement_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{width: `${Math.min(c.achievement_pct, 100)}%`}}
                    data-testid={`progress-bar-${idx}`}
                  />
                </div>
                {c.balance_qty > 0 && (
                  <p className="text-xs text-text-muted">Balance: {c.balance_qty.toLocaleString()} pcs remaining</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {salesTarget && (!salesTarget.customers || salesTarget.customers.length === 0) && (
        <div className="bg-white border border-border rounded-sm p-8 text-center" data-testid="no-sales-target">
          <Target className="w-8 h-8 text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-muted">No schedules found for the current month</p>
        </div>
      )}

      {/* Recent Dispatches */}
      {stats?.recent_dispatches?.length > 0 && (
        <div className="bg-white border border-border rounded-sm">
          <div className="p-4 border-b border-border">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Recent Dispatches</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer</th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Qty</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_dispatches.map((d, idx) => (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-4 font-mono text-xs">{d.date ? new Date(d.date).toLocaleDateString() : '-'}</td>
                    <td className="p-4 font-mono text-xs font-semibold">{d.part_id}</td>
                    <td className="p-4 text-xs">{d.customer}</td>
                    <td className="p-4 text-right font-mono text-xs">{d.qty?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BatchReceiveDialog
        open={batchReceive.open}
        vendorName={batchReceive.vendor}
        entries={batchReceive.entries}
        onClose={() => setBatchReceive({ open: false, vendor: '', entries: [] })}
        onDone={fetchDashboard}
      />
    </div>
  );
}
